/**
 * Where this visit came from, captured once when the site loads. A demo request made
 * pages later still names the page and referrer that brought the visitor, since
 * navigating inside the SPA would otherwise leave only the current page.
 */
export interface VisitSource {
  landing_page: string;
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

const STORAGE_KEY = 'taskeye-visit-source';

let capturedSource: VisitSource | null = null;

function externalReferrer(): string | undefined {
  try {
    // A referrer on our own origin is a reload or a page we linked to, not a source.
    const origin = new URL(document.referrer).origin;
    return origin === window.location.origin ? undefined : document.referrer;
  } catch {
    return undefined;
  }
}

function sourceFromLocation(): VisitSource {
  const params = new URLSearchParams(window.location.search);
  return {
    landing_page: window.location.pathname + window.location.search,
    referrer: externalReferrer(),
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
  };
}

function isVisitSource(value: unknown): value is VisitSource {
  return typeof value === 'object' && value !== null
    && typeof (value as { landing_page?: unknown }).landing_page === 'string';
}

/**
 * Records the first page of this browser session. Kept in sessionStorage so a reload
 * keeps the original landing page; when storage is blocked, the in-memory copy still
 * covers the rest of this page load.
 */
export function captureVisitSource(): void {
  try {
    const stored: unknown = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null');
    if (isVisitSource(stored)) {
      capturedSource = stored;
      return;
    }
    capturedSource = sourceFromLocation();
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(capturedSource));
  } catch {
    capturedSource = capturedSource ?? sourceFromLocation();
  }
}

export function getVisitSource(): VisitSource {
  return capturedSource ?? sourceFromLocation();
}
